import React, { useState } from 'react';
import { Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import {
  IconUser,
  IconMail,
  IconPhone,
  IconBuildingStore,
  IconCheck,
  IconSparkles,
  IconShieldCheck,
  IconId,
} from '@tabler/icons-react';
import { quickSignup } from '../../Users/services/UserServicesV2';
import { validateCPF, validateCNPJ } from '../../Customers/utils';
import ModalComponent from '../../../jsx/components/Modal';
import NewTermsOfUse from '../../../jsx/components/ModalData/NewTermsOfUse';
import TurnstileWidget from '../../../components/TurnstileWidget';
import { useTurnstile } from '../../../hooks/useTurnstile';
import Swal from 'sweetalert2';

const FreeRegistrationForm = ({ planConfig, onRegisterSuccess }) => {
  const config = {
    usage_type: planConfig?.usage_type ?? 1,
    plan_id: planConfig?.plan_id ?? 7,
    account_type: planConfig?.account_type ?? '2',
    planName: planConfig?.planName ?? 'Starter',
    planBadgeVariant: planConfig?.planBadgeVariant ?? 'success',
    submitText: planConfig?.submitText ?? 'Começar teste grátis',
    trialDays: planConfig?.trialDays ?? 7,
  };
  const [processing, setProcessing] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Turnstile integration
  const { config: turnstileConfig, shouldShow: shouldShowTurnstile } = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const [turnstileResetTrigger, setTurnstileResetTrigger] = useState(0);

  const handleTurnstileSuccess = (token) => {
    setTurnstileToken(token);
    setTurnstileError(null);
  };

  const handleTurnstileError = (error) => {
    setTurnstileError(error);
    setTurnstileToken(null);
  };

  const handleTurnstileExpired = () => {
    setTurnstileToken(null);
    setTurnstileError("Token de segurança expirado. Tente novamente.");
  };

  const resetTurnstile = () => {
    setTurnstileResetTrigger(prev => prev + 1);
    setTurnstileToken(null);
    setTurnstileError(null);
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      person_type: 2,
      company_name: '',
      first_name: '',
      cpf: '',
      cnpj: '',
      email: '',
      email_confirm: '',
      phone: '',
      terms_accepted: false,
    },
  });

  const personType = watch('person_type');

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 6) {
      value = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
    } else if (value.length > 2) {
      value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    }
    setValue('phone', value, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    // Validar Turnstile se habilitado
    if (shouldShowTurnstile && turnstileConfig?.enabled && !turnstileToken) {
      Swal.fire({
        title: "Verificação de Segurança",
        text: "Complete a verificação de segurança antes de continuar.",
        icon: "warning",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    setProcessing(true);

    try {
      // Determinar cpf_cnpj baseado no person_type
      const cpfCnpj = data.person_type === 2
        ? (data.cpf || '').replace(/\D/g, '')
        : (data.cnpj || '').replace(/\D/g, '');

      // Para PF, first_name vem do company_name (nome completo)
      const firstName = data.person_type === 2
        ? data.company_name.trim().split(' ')[0]
        : data.first_name.trim();

      const payload = {
        company_name: data.company_name.trim(),
        first_name: firstName,
        email: data.email.trim().toLowerCase(),
        phone: data.phone.replace(/\D/g, ''),
        account_type: config.account_type,
        usage_type: config.usage_type,
        person_type: data.person_type,
        cpf_cnpj: cpfCnpj,
      };

      if (config.plan_id) {
        payload.plan_id = config.plan_id;
      }

      // Adicionar token Turnstile se disponível
      if (turnstileToken) {
        payload.turnstile_token = turnstileToken;
      }

      const response = await quickSignup(payload);

      if (response?.data?.success) {
        // Track Facebook Pixel - Lead gratuito
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: 0,
            currency: 'BRL',
            num_items: 1,
            content_type: 'product',
            contents: [
              { id: `plano-${config.planName.toLowerCase()}`, quantity: 1, item_price: 0 }
            ],
          });
        }

        // Se houver callback (novo fluxo com onboarding), delega ao pai
        if (onRegisterSuccess) {
          onRegisterSuccess({
            accountId: response.data.account?.id,
            signupToken: response.data.signup_token,
            userEmail: data.email,
            trialDays: config.trialDays,
          });
          return;
        }

        // Fluxo antigo (fallback): SweetAlert terminal
        const trialMsg = config.trialDays
          ? `<p style="margin-top: 8px; font-size: 14px; color: #3b82f6; font-weight: 500;">Seu período de teste de ${config.trialDays} dias começa após a ativação.</p>`
          : '';
        Swal.fire({
          icon: 'success',
          title: 'Conta criada com sucesso!',
          html: `
            <p style="margin-bottom: 8px;">Enviamos um e-mail de ativação para:</p>
            <strong>${data.email}</strong>
            <p style="margin-top: 12px; font-size: 14px; color: #666;">
              Verifique sua caixa de entrada (e spam) para ativar sua conta.
            </p>
            ${trialMsg}
          `,
          confirmButtonText: 'Ir para o Login',
          confirmButtonColor: '#3b82f6',
          allowOutsideClick: false,
        }).then(() => {
          window.location.href = '/login';
        });
      }
    } catch (error) {
      console.error('Erro no cadastro free:', error);

      let errorMessage = 'Tente novamente em alguns instantes.';

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.details) {
          const details = errorData.details;
          if (details.email) errorMessage = Array.isArray(details.email) ? details.email[0] : details.email;
          else if (details.phone) errorMessage = Array.isArray(details.phone) ? details.phone[0] : details.phone;
          else if (details.cpf_cnpj) errorMessage = Array.isArray(details.cpf_cnpj) ? details.cpf_cnpj[0] : details.cpf_cnpj;
          else {
            const firstKey = Object.keys(details)[0];
            if (firstKey) {
              const val = details[firstKey];
              errorMessage = Array.isArray(val) ? val[0] : val;
            }
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Erro ao criar conta',
        text: errorMessage,
        confirmButtonColor: '#3b82f6',
      });

      // Reset Turnstile em caso de erro
      if (shouldShowTurnstile) {
        resetTurnstile();
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="signup-free-form">
      <div className="signup-free-form__card">
        <div className="signup-free-form__header">
          <div className={`signup-free-form__plan-badge signup-free-form__plan-badge--${config.planBadgeVariant}`}>
            <IconSparkles size={16} />
            Plano {config.planName}
            {config.trialDays && ` — ${config.trialDays} dias grátis`}
          </div>
          <p>Preencha seus dados para começar a usar a plataforma</p>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Tipo de Pessoa */}
          <Form.Group className="mb-3">
            <Form.Label>
              <IconUser size={16} className="me-2" />
              Tipo de Pessoa
            </Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                name="person_type"
                id="person_type_pf_free"
                label="Pessoa Física (PF)"
                checked={personType === 2}
                onChange={() => setValue('person_type', 2)}
                disabled={processing}
              />
              <Form.Check
                type="radio"
                name="person_type"
                id="person_type_pj_free"
                label="Pessoa Jurídica (PJ)"
                checked={personType === 1}
                onChange={() => setValue('person_type', 1)}
                disabled={processing}
              />
            </div>
            <input type="hidden" {...register('person_type')} />
          </Form.Group>

          {/* Nome da Empresa / Nome Completo */}
          <Form.Group className="mb-3">
            <Form.Label>
              <IconBuildingStore size={16} className="me-2" />
              {personType === 1 ? 'Nome da Empresa' : 'Nome Completo'}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={personType === 1 ? 'Ex: Minha Empresa LTDA' : 'Ex: João Silva'}
              {...register('company_name', {
                required: personType === 1 ? 'Nome da empresa é obrigatório' : 'Nome completo é obrigatório',
                maxLength: { value: 128, message: 'Máximo 128 caracteres' },
              })}
              disabled={processing}
              isInvalid={!!errors.company_name}
            />
            {errors.company_name && (
              <Form.Control.Feedback type="invalid">
                {errors.company_name.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* CPF - Pessoa Física */}
          {personType === 2 && (
            <Form.Group className="mb-3">
              <Form.Label>
                <IconId size={16} className="me-2" />
                CPF
              </Form.Label>
              <NumberFormat
                className={`form-control ${errors.cpf ? 'is-invalid' : ''}`}
                format="###.###.###-##"
                mask="_"
                placeholder="000.000.000-00"
                onValueChange={(values) => {
                  setValue('cpf', values.value, { shouldValidate: true });
                }}
                value={watch('cpf')}
                disabled={processing}
              />
              <input
                type="hidden"
                {...register('cpf', {
                  required: personType === 2 ? 'CPF é obrigatório' : false,
                  validate: (value) => {
                    if (personType !== 2) return true;
                    if (!value) return 'CPF é obrigatório';
                    if (!validateCPF(value)) {
                      return 'CPF inválido';
                    }
                    return true;
                  },
                })}
              />
              {errors.cpf && (
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                  {errors.cpf.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          )}

          {/* CNPJ - Pessoa Jurídica */}
          {personType === 1 && (
            <Form.Group className="mb-3">
              <Form.Label>
                <IconId size={16} className="me-2" />
                CNPJ
              </Form.Label>
              <NumberFormat
                className={`form-control ${errors.cnpj ? 'is-invalid' : ''}`}
                format="##.###.###/####-##"
                mask="_"
                placeholder="00.000.000/0000-00"
                onValueChange={(values) => {
                  setValue('cnpj', values.value, { shouldValidate: true });
                }}
                value={watch('cnpj')}
                disabled={processing}
              />
              <input
                type="hidden"
                {...register('cnpj', {
                  required: personType === 1 ? 'CNPJ é obrigatório' : false,
                  validate: (value) => {
                    if (personType !== 1) return true;
                    if (!value) return 'CNPJ é obrigatório';
                    if (!validateCNPJ(value)) {
                      return 'CNPJ inválido';
                    }
                    return true;
                  },
                })}
              />
              {errors.cnpj && (
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                  {errors.cnpj.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          )}

          {/* Nome do Responsável - Apenas PJ */}
          {personType === 1 && (
            <Form.Group className="mb-3">
              <Form.Label>
                <IconUser size={16} className="me-2" />
                Nome do Responsável
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Maria Santos"
                {...register('first_name', {
                  required: personType === 1 ? 'Nome do responsável é obrigatório' : false,
                  maxLength: { value: 70, message: 'Máximo 70 caracteres' },
                })}
                disabled={processing}
                isInvalid={!!errors.first_name}
              />
              {errors.first_name && (
                <Form.Control.Feedback type="invalid">
                  {errors.first_name.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          )}

          <Row>
            <Col md={12}>
              {/* Email */}
              <Form.Group className="mb-3">
                <Form.Label>
                  <IconMail size={16} className="me-2" />
                  E-mail
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="seu@email.com"
                  {...register('email', {
                    required: 'E-mail é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'E-mail inválido',
                    },
                  })}
                  disabled={processing}
                  isInvalid={!!errors.email}
                />
                {errors.email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={12}>
              {/* Confirmar Email */}
              <Form.Group className="mb-3">
                <Form.Label>
                  <IconMail size={16} className="me-2" />
                  Confirmar e-mail
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Repita seu e-mail"
                  {...register('email_confirm', {
                    required: 'Confirmação de e-mail é obrigatória',
                    validate: (value) => {
                      if (value.toLowerCase() !== watch('email').toLowerCase()) {
                        return 'Os e-mails não coincidem';
                      }
                      return true;
                    },
                  })}
                  disabled={processing}
                  isInvalid={!!errors.email_confirm}
                  onPaste={(e) => e.preventDefault()}
                />
                {errors.email_confirm && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email_confirm.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Telefone */}
          <Form.Group className="mb-3">
            <Form.Label>
              <IconPhone size={16} className="me-2" />
              Telefone
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="(11) 99999-9999"
              {...register('phone', {
                required: 'Telefone é obrigatório',
                validate: (value) => {
                  const digits = value.replace(/\D/g, '');
                  if (digits.length < 10 || digits.length > 11) {
                    return 'Telefone inválido (DDD + número)';
                  }
                  return true;
                },
              })}
              onChange={handlePhoneChange}
              maxLength={15}
              disabled={processing}
              isInvalid={!!errors.phone}
            />
            {errors.phone && (
              <Form.Control.Feedback type="invalid">
                {errors.phone.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Termos */}
          <Form.Group className="mb-4">
            <Form.Check
              type="checkbox"
              id="terms-free"
              label={
                <span style={{ fontSize: '0.8125rem' }}>
                  Li e concordo com os{' '}
                  <span
                    className="signup-terms-link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowTermsModal(true);
                    }}
                  >
                    Termos de Uso
                  </span>
                </span>
              }
              {...register('terms_accepted', {
                required: 'Você precisa aceitar os termos de uso',
              })}
              disabled={processing}
              isInvalid={!!errors.terms_accepted}
              feedback={errors.terms_accepted?.message}
              feedbackType="invalid"
            />
          </Form.Group>

          {/* Turnstile Widget */}
          {shouldShowTurnstile && (
            <div className="mb-3">
              <TurnstileWidget
                onTokenChange={handleTurnstileSuccess}
                onError={handleTurnstileError}
                onExpired={handleTurnstileExpired}
                resetTrigger={turnstileResetTrigger}
                className="checkout-turnstile"
              />
              {turnstileError && (
                <Alert variant="danger" className="mt-2">
                  <IconShieldCheck size={16} className="me-2" />
                  {turnstileError}
                </Alert>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className="signup-plan-card__cta signup-plan-card__cta--success w-100"
            disabled={processing || (shouldShowTurnstile && !turnstileToken)}
          >
            {processing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Criando sua conta...
              </>
            ) : shouldShowTurnstile && !turnstileToken ? (
              "Complete a verificação de segurança"
            ) : (
              <>
                <IconCheck size={20} className="me-2" />
                {config.submitText}
              </>
            )}
          </button>

          <div className="signup-free-form__secure">
            <IconShieldCheck size={14} />
            <span>Seus dados estão seguros conosco</span>
          </div>
        </Form>
      </div>

      {/* Terms Modal */}
      <ModalComponent
        props={{
          title: "Termos de Uso",
          content: <NewTermsOfUse />,
          showModal: showTermsModal,
          setShowModal: setShowTermsModal,
          size: "xl",
        }}
      />
    </div>
  );
};

export default FreeRegistrationForm;
